<template>
  <div class="side-bar" id="side-bar" @scroll="onScrollHandler">
    <!-- side menu toggle -->
    <div class="side-bar__toggle">
      <b-btn @click="toggleSideBar" class="side-bar__toggleBtn">
        <span v-if="sideBarExpanded">
          <open-icon name="collapse-left" />
        </span>
        <span v-else>
          <open-icon name="collapse-right" />
        </span>
      </b-btn>
    </div>

    <div class="side-bar__container container">
      <!-- Admin links -->

      <div v-show="showAdminMenu">
        <h3 class="mt-3">Admin</h3>

        <nav class="side-bar__nav">
          <ul>
            <li>
              <router-link to="/admin/questionnaire">
                <open-icon name="clipboard" />
                Questionnaires
              </router-link>
            </li>
            <li>
              <router-link to="/admin/question">
                <open-icon name="question-mark" />
                Questions
              </router-link>
            </li>
            <li>
              <router-link to="/admin/job-question">
                <open-icon name="question-mark" />
                Job Questions
              </router-link>
            </li>
            <li>
              <router-link to="/admin/society">
                <open-icon name="list" />
                Societies
              </router-link>
            </li>
            <li>
              <router-link to="/admin/specialty">
                <i class="mdi mdi-doctor" aria-hidden="true" />
                Specialties
              </router-link>
            </li>
            <li>
              <router-link to="/admin/master-specialty">
                <i class="mdi mdi-doctor" aria-hidden="true" />
                Master Specialties
              </router-link>
            </li>
            <li>
              <router-link to="/admin/subspecialty">
                <open-icon name="pulse" />
                Subspecialties
              </router-link>
            </li>
            <li>
              <router-link to="/admin/master-subspecialty">
                <open-icon name="pulse" />
                Master Subspecialties
              </router-link>
            </li>
            <li>
              <router-link to="/admin/user">
                <open-icon name="people" />
                Users
              </router-link>
            </li>
            <li>
              <router-link to="/admin/user-emails">
                <open-icon name="people" />
                User Emails
              </router-link>
            </li>
            <li>
              <router-link to="/admin/survey">
                <open-icon name="spreadsheet" />
                Studies
              </router-link>
            </li>
            <li>
              <router-link to="/admin/internal-persons">
                <open-icon name="people" />
                Internal Persons
              </router-link>
            </li>
            <li>
              <router-link to="/admin/job-offers">
                <open-icon name="document" />
                Job Offers
              </router-link>
            </li>
            <li>
              <router-link to="/admin/settings">
                <open-icon name="code" />
                Settings
              </router-link>
            </li>
          </ul>
        </nav>

        <!-- admin options -->

        <b-form-checkbox
          switch
          v-model="$store.state.userSettings.demoMode"
          :disabled="!isDemoMode"
          :value="1"
          :unchecked-value="0"
        >
          Demo Mode
        </b-form-checkbox>
        <b-form-checkbox
          switch
          v-model="$store.state.userSettings.showAllResults"
        >
          Show All Results
        </b-form-checkbox>

        <!-- admin help -->

        <b-button variant="primary" class="float-right my-3" id="admin-help">
          Help
        </b-button>
        <b-popover triggers="click" :target="'admin-help'" placement="bottom">
          <dl>
            <dt>Demo Mode</dt>
            <dd>
              Displays simulated UI elements that are not fully functional yet,
              mainly on the Dashboard page.
            </dd>
            <dt>Show All Results</dt>
            <dd>
              This option will display all survey results, even if insufficient
              data is present.
              <br />
              This option is only available to admin users.
            </dd>
          </dl>
        </b-popover>
      </div>

      <!-- Regular user links -->

      <div v-show="!showAdminMenu">
        <div
          class="side-bar__identity col-12"
          v-bind:class="{ 'side-bar__menu--expanded': sideBarExpanded }"
        >
          <div class="my-3">
            <h4 class="mb-0">{{ userName }}</h4>
            <div>{{ userEmail }}</div>
          </div>

          <div class="mb-3 d-none d-md-block">
            <div class="small-heading">My Society</div>
            <div>{{ userSociety }}</div>
          </div>

          <div class="mb-1 d-none d-md-block">
            <div class="small-heading">My Specialty</div>
            <div>{{ userSpecialty }}</div>
          </div>

          <!-- TODO: make this show up smaller/floated right in mobile view, as above two items are hidden (?) -->
          <b-btn
            size="sm"
            variant="primary"
            class="mt-0 mb-3 float-right side-bar__profileBtn"
            href="/#/profile"
          >
            <open-icon name="person" />
            Edit Profile
          </b-btn>

          <div style="clear: both;"></div>
        </div>

        <div
          class="side-bar__navContainer"
          v-bind:class="{
            'side-bar__navContainer--expanded': mobileNavExpanded,
          }"
        >
          <b-btn
            @click="toggleMobileNav"
            class="side-bar__toggleNavBtn btn-primary"
          >
            Quicklinks
            <open-icon name="caret-bottom" class="float-right" />
          </b-btn>
          <nav class="side-bar__nav">
            <ul>
              <li>
                <router-link to="/filters">
                  <open-icon name="pie-chart" />
                  My Charts
                </router-link>
              </li>
              <li>
                <router-link to="/surveyHome">
                  <open-icon name="clipboard" />
                  Survey History
                </router-link>
              </li>
              <li v-if="$store.state.userSettings.demoMode">
                <router-link to="/resources">
                  <open-icon name="document" />
                  Resources
                </router-link>
              </li>
              <li v-if="$store.state.userSettings.demoMode">
                <router-link to="/help">
                  <open-icon name="question-mark" />
                  Help
                </router-link>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import Vue from 'vue';
export default Vue.extend({
  name: 'SideBar',
  computed: {
    showAdminMenu: function () {
      return this.$route.path.indexOf('/admin') === 0;
    },
    userEmail: function () {
      return this.$store.state.user ? this.$store.state.user.email : '';
    },
    userName: function () {
      const user = this.$store.state.user;
      return user
        ? `${user.first_name ? user.first_name : ''} ${
            user.last_name ? user.last_name : ''
          }`
        : '';
    },
    userSociety: function () {
      const user = this.$store.state.user;
      return user && user.society_id ? user.society.name : '';
    },
    userSpecialty: function () {
      const user = this.$store.state.user;
      return user && user.specialty_id ? user.specialty.title : '';
    },
    sideBarExpanded: function () {
      return this.$store.state.sideBarExpanded;
    },
    mobileNavExpanded: function () {
      return this.$store.state.mobileNavExpanded;
    },
    isDemoMode: function () {
      return this.$store.getters.isDemoMode;
    },
  },
  methods: {
    toggleSideBar: function () {
      this.$store.state.sideBarExpanded = !this.$store.state.sideBarExpanded;
    },
    toggleMobileNav: function () {
      this.$store.state.mobileNavExpanded = !this.$store.state
        .mobileNavExpanded;
    },
    onScrollHandler: function (event) {
      const sideBar = event.target.value;
      const sideBar_classes = sideBar.classList;
      if (document.documentElement.scrollTop >= 56) {
        if (sideBar_classes.contains('side-bar--fixed') === false) {
          sideBar_classes.toggle('side-bar--fixed');
        }
      } else {
        if (sideBar_classes.contains('side-bar--fixed') === true) {
          sideBar_classes.toggle('side-bar--fixed');
        }
      }
    },
  },
});
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">
@import '../../sass/variables_mixins.scss';

$side-menu-width: 284px;

.side-bar {
  //padding:1em;
  font-size: 0.85rem;
}

.side-bar__nav {
  ul {
    padding-left: 0;
  }

  li {
    list-style: none;
  }

  a {
    //font-size: 14px;
  }

  i.mdi {
    font-size: 18px;
    line-height: 1;
    width: calc(14px + 0.85rem);
    display: inline-block;
    position: relative;
    top: 2px;
  }

  svg.icon {
    height: 14px;
    width: 14px;
    margin-right: 1em;
    vertical-align: text-top;

    use {
      fill: #333;
    }
  }
}

.btn.side-bar__profileBtn {
  svg.icon {
    height: 12px;
    width: 12px;
  }
}

@media (max-width: 767px) {
  .side-bar {
    margin-bottom: 2em;
    padding-top: 2em;
    padding-bottom: 2em;
    position: relative;

    &:after {
      content: '';
      display: block;
      bottom: 0;
      @include horizontal-center;
      width: 80px;
      height: 1px;
      background-color: #aaa;
    }
  }

  .side-bar__navContainer {
    max-width: 250px;
    margin-left: auto;
    margin-right: auto;
    margin-top: 1em;
  }

  .side-bar__nav {
    max-height: 0;
    @include transition(all 0.8s ease-out);
    overflow: hidden;
    background-color: #fff;

    ul {
      margin-bottom: 0;
    }

    a {
      padding: 0.5em 1em;
      display: block;
      letter-spacing: 2px;
      @include transition(all 0.2s ease-out);

      &:hover {
        text-decoration: none;
        background-color: #ddd;
        color: #000;
      }
    }
  }

  .side-bar__navContainer--expanded {
    .side-bar__nav {
      max-height: 800px;
    }

    .side-bar__toggleNavBtn {
      border-radius: $border-radius $border-radius 0 0;
    }
  }

  .side-bar__toggle {
    display: none;
  }

  .side-bar__toggleNavBtn {
    @include transition(all 0.8s ease-in-out);
    width: 100%;
    padding: 0.5em 1em;
    background-color: $green;

    &:focus {
      box-shadow: none !important;
    }
  }

  .side-bar__identity {
    background-color: #fff;
    padding: 1em;
    @include border;
  }
}

@media (min-width: 768px) {
  .main-content {
    @include transition(padding 0.3s);
  }

  .side-bar__toggleNavBtn {
    display: none !important;
  }

  .side-bar {
    width: $side-menu-width;
    background-color: #fff;
    position: absolute;
    left: 0;
    top: 56px;
    @include transform(translateX(-100%));
    z-index: 999;
    height: $phairify_main_content_height;
    //overflow-x: hidden;
    @include transition(transform 0.3s);
  }
  .side-bar--expanded {
    .side-bar {
      @include transform(translateX(0));
    }

    .main-content {
      padding-left: $side-menu-width;

      .footer {
        left: $side-menu-width;
      }
    }

    .side-bar__toggle {
      width: $side-menu-width;
      max-width: $side-menu-width;
      left: 0;

      svg.icon {
        @include vertical-center;
        right: 10px;
        left: auto;
      }
    }
  }

  .side-bar--fixed {
    position: fixed;
    top: 0;
    left: 0;
  }

  .side-bar__toggle {
    @include transition(all 0.3s);
    width: 30px;
    max-width: 100px;
    height: 28px;
    font-size: 14px;
    background-color: $green;
    position: absolute;
    top: 0;
    left: 100%;
    z-index: 100;
    display: flex;
    justify-content: flex-end;

    svg.icon,
    i.mdi {
      font-size: 18px;
      height: 18px;
      width: 18px;
      position: absolute;
      @include absolute-center;

      use {
        fill: #fff;
      }
    }
  }
  .btn.side-bar__toggleBtn {
    padding: 0 10px;
    background-color: transparent;

    svg.icon {
      @include transition(all 0.3s);
    }

    &:hover {
      background-color: transparent;

      svg.icon {
        @include transform(translate(-5px, -50%));
      }
    }

    &:active,
    &:focus {
      outline: none;
      box-shadow: none;
    }
  }

  .side-bar__container {
    overflow-y: auto;
    padding-top: 2em;
  }

  .side-bar__nav {
    a {
      color: #333;
      display: block;
      background-color: $green;
      padding: 0.75em 1em;
      margin-bottom: 5px;
      @include font-questrial;
      @include transition(all 0.2s ease-out);

      &:hover {
        background-color: lighten($green, 20%);
        color: #000;
        text-decoration: none;
      }

      &.router-link-exact-active {
        background-color: darken($green, 30%);
        color: #fff;

        svg use {
          fill: #fff;
        }
      }
    }
  }
}
</style>
